import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import RichHtml from "@/components/RichHtml";
import VerseText from "@/components/VerseText";

export default function Hero({ content }) {
  const scrollNext = () => {
    const el = document.getElementById("schedule");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-[100svh] bg-[#FAF9F6] overflow-hidden">
      <div className="noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-6">
              <span className="w-8 h-px bg-[#D4A373]" />
              A Message From Our Pastor
            </div>

            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1C201E]">
              {content?.title || "Welcome to Bible Baptist Church Barani"}
            </h1>

            <p className="mt-8 text-lg text-[#1C201E]/85 leading-relaxed max-w-2xl">
              <RichHtml html={content?.message} testId="hero-message-content" className="prose-p:my-0" />
            </p>

            {content?.verse && (
              <blockquote className="mt-8 pl-5 border-l-2 border-[#D4A373] font-serif-display text-xl text-[#2C4C3B] italic leading-snug">
                <VerseText testId="hero-verse-linked" text={content.verse} />
              </blockquote>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-cta-visit"
                onClick={() => document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth" })}
                className="px-6 py-3 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] transition-colors duration-200"
              >
                Plan Your Visit
              </button>
              <button
                data-testid="hero-cta-salvation"
                onClick={() => document.getElementById("salvation")?.scrollIntoView({ behavior: "smooth" })}
                className="px-6 py-3 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm hover:bg-[#2C4C3B] hover:text-white transition-colors duration-200"
              >
                Know Christ
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D4A373]/20 rounded-[2rem] -rotate-3" />
              <div className="relative rounded-[2rem] overflow-hidden bg-[#F3EFEA] aspect-[4/5] shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
                <img
                  data-testid="pastor-photo"
                  src={content?.pastor_photo || `${(process.env.REACT_APP_WEB_URL || "https://bbcbarani.org").replace(/\/$/, "")}/assets/img/psr2.png`}
                  alt="Pastor"
                  className="w-full h-full object-cover object-top"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div className="mt-4 text-right">
                <div className="font-serif-display text-2xl text-[#1C201E]">{content?.pastor_name || "Pastor"}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mt-1">Bible Baptist Church Barani</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <button
        data-testid="hero-scroll-indicator"
        onClick={scrollNext}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#5C6661] hover:text-[#2C4C3B] transition-colors"
        aria-label="Scroll down"
      >
        <ChevronDown className="w-6 h-6 animate-bounce" />
      </button>
    </section>
  );
}
